// Config for the two knowledge hubs that reuse the guides infrastructure.

export const SCAM_HUB = {
  key: "scam",
  base: "/scam-hub",
  label: "Scam Hub",
  backLabel: "All Scam Hub articles",
  icon: "shield-warning",
  eyebrow: "Protect your money",
  h1: "Tipster Scam Hub",
  intro: "Fake betting tipsters follow the same playbook every time. This hub breaks down exactly how tipster scams work — fake results, doctored screenshots, deleted losers, social-media cons and dodgy subscriptions — so you can spot them a mile off and keep your money safe.",
  seoTitle: "Tipster Scam Hub: Spot Fake Betting Tipsters",
  seoDescription: "In-depth guides on how betting tipster scams work — fake results, doctored screenshots, deleted losers, Telegram cons and subscription scams — and how to protect yourself.",
  sources: [
    { name: "Gambling Commission (UK regulator)", url: "https://www.gamblingcommission.gov.uk/public-and-players", note: "check a betting operator is licensed and report illegal gambling." },
    { name: "BeGambleAware", url: "https://www.begambleaware.org/", note: "free, confidential advice and support for anyone affected by gambling." },
    { name: "Citizens Advice — problem debt & scams", url: "https://www.citizensadvice.org.uk/consumer/scams/check-if-something-might-be-a-scam/", note: "how to check if something is a scam and what to do if you've paid." },
  ],
};

export const EVALUATE_HUB = {
  key: "evaluate",
  base: "/how-to-evaluate-a-tipster",
  label: "How to Evaluate a Tipster",
  backLabel: "All evaluation guides",
  icon: "chart-line-up",
  eyebrow: "Judge the numbers",
  h1: "How to Evaluate a Tipster",
  intro: "Learn to judge a betting tipster like a pro. This hub explains the numbers that actually matter — ROI, yield, strike rate, drawdown, sample size, closing line value and more — so you can tell genuine long-term edge from a lucky streak or a sales pitch.",
  seoTitle: "How to Evaluate a Tipster: ROI, Yield & Strike Rate",
  seoDescription: "Learn how to properly evaluate a betting tipster — calculate ROI and yield, read strike rate, understand drawdown and sample size, and verify results before you subscribe.",
};

export const BASICS_HUB = {
  key: "basics",
  base: "/betting-basics",
  label: "Betting Basics",
  backLabel: "All betting basics",
  icon: "book-open",
  eyebrow: "Start here",
  h1: "Betting Basics",
  intro: "New to betting? Start here. This hub explains how betting actually works — how odds and payouts are calculated, the different odds formats, the bookmaker's built-in margin and how to read a bet slip — so you understand exactly what you're risking.",
  seoTitle: "Betting Basics: How Odds, Payouts & Bet Slips Work",
  seoDescription: "A plain-English guide to how betting works — how odds and payouts are calculated, decimal vs fractional vs American odds, the bookmaker's margin (overround) and how to read a bet slip.",
};

export const SAFETY_HUB = {
  key: "safety",
  base: "/betting-safety",
  label: "Betting Safety & Law",
  backLabel: "All safety & law guides",
  icon: "shield-check",
  eyebrow: "Know your rights",
  h1: "Betting Safety & Law (UK)",
  intro: "Bet with confidence and know where you stand. This hub covers the UK legal picture — whether tipsters are legal, tax on winnings, self-exclusion with GAMSTOP and how to get a refund if you're mis-sold — so you can protect your money and yourself.",
  seoTitle: "Betting Safety & Law UK: Legality, Tax, GAMSTOP",
  seoDescription: "Your UK betting rights explained — are tipsters legal, do you pay tax on winnings, how to self-exclude with GAMSTOP and how to get a refund from a betting tipster.",
  sources: [
    { name: "GAMSTOP", url: "https://www.gamstop.co.uk/", note: "free self-exclusion from all UK-licensed online gambling in one place." },
    { name: "Gambling Commission", url: "https://www.gamblingcommission.gov.uk/public-and-players", note: "the UK regulator — your rights, licensed operators and how to complain." },
    { name: "GamCare", url: "https://www.gamcare.org.uk/", note: "the National Gambling Helpline (0808 8020 133) and live support." },
    { name: "HMRC — gambling & tax", url: "https://www.gov.uk/", note: "official UK government guidance (betting winnings are not taxed for punters)." },
  ],
};

export const PLATFORMS_HUB = {
  key: "platforms",
  base: "/tipster-platforms",
  label: "Tipster Platforms",
  backLabel: "All platform guides",
  icon: "device-mobile",
  eyebrow: "Where tipsters operate",
  h1: "Tipster Platforms",
  intro: "Tipsters live on Telegram, Instagram, proofing sites and beyond — and each platform has its own risks. This hub shows you how to tell genuine services from scams on every channel, and where to find tipsters you can actually trust.",
  seoTitle: "Tipster Platforms: Telegram, Instagram & Proofing Sites",
  seoDescription: "How betting tipsters operate on Telegram, Instagram and proofing sites — how to verify each channel, spot scams and find genuine tipsters worth following.",
};

export const CHOOSING_HUB = {
  key: "choosing",
  base: "/choosing-a-tipster",
  label: "Choosing a Tipster",
  backLabel: "All choosing guides",
  icon: "list-checks",
  eyebrow: "Make the right call",
  h1: "Choosing a Tipster",
  intro: "Picking a tipster well is mostly about filtering out the bad ones. This hub gives you a clear, evidence-based process — how to choose, free vs paid, how much to pay, and the exact questions to ask before you subscribe.",
  seoTitle: "Choosing a Tipster: How to Pick One Worth Paying For",
  seoDescription: "An evidence-based guide to choosing a betting tipster — how to shortlist and pick one, free vs paid, how much to pay, and the questions to ask before subscribing.",
};

export const PSYCHOLOGY_HUB = {
  key: "psychology",
  base: "/betting-psychology",
  label: "Betting Psychology",
  backLabel: "All psychology guides",
  icon: "brain",
  eyebrow: "Master your mindset",
  h1: "Betting Psychology",
  intro: "Discipline beats tips. This hub explains why most bettors lose, how loss-chasing and the gambler's fallacy sabotage good decisions, and the staking, record-keeping and tilt-control habits that let a genuine edge survive.",
  seoTitle: "Betting Psychology: Why Bettors Lose & How to Stay Disciplined",
  seoDescription: "The mindset behind winning and losing — why most bettors lose, how to stop chasing losses, the gambler's fallacy explained, and the discipline that protects your edge.",
};

export const BEATBOOKIE_HUB = {
  key: "beatbookie",
  base: "/beat-the-bookmaker",
  label: "Beat the Bookmaker",
  backLabel: "All beat-the-bookmaker guides",
  icon: "target",
  eyebrow: "Get an edge",
  h1: "Beat the Bookmaker",
  intro: "Serious tactics for serious bettors. This hub covers the edges that actually move the needle — line shopping for the best odds, understanding closing line value, why bookmakers restrict winners, Best Odds Guaranteed and using exchanges to beat the margin.",
  seoTitle: "Beat the Bookmaker: Best Odds, CLV & Exchanges",
  seoDescription: "Learn to beat the bookmaker — line shopping for the best odds, closing line value, why accounts get restricted, Best Odds Guaranteed, and using betting exchanges to beat the margin.",
};

export const SPORTSTRATEGY_HUB = {
  key: "sportstrategy",
  base: "/sport-betting-strategy",
  label: "Sport Betting Strategy",
  backLabel: "All sport strategy guides",
  icon: "strategy",
  eyebrow: "Bet smarter by sport",
  h1: "Sport Betting Strategy",
  intro: "Sport-by-sport strategy to bet with an edge. This hub covers how to bet on football, horse racing, tennis and golf, plus how to read form and stats and approach in-play betting with discipline.",
  seoTitle: "Sport Betting Strategy: Football, Racing, Tennis & Golf",
  seoDescription: "Sport-specific betting strategy guides — how to bet on football, horse racing, tennis and golf, how to read form and stats, and a disciplined in-play betting strategy.",
};

export const BANKROLL_HUB = {
  key: "bankroll",
  base: "/bankroll-and-staking",
  label: "Bankroll & Staking",
  backLabel: "All bankroll & staking guides",
  icon: "coins",
  eyebrow: "Protect your bank",
  h1: "Bankroll & Staking",
  intro: "How you stake matters as much as what you bet. This hub covers building a betting bankroll, staking plans compared, the Kelly criterion, how much to bet per bet, surviving losing runs and how points and units work.",
  seoTitle: "Bankroll & Staking: Plans, Kelly & Bet Sizing",
  seoDescription: "Master bankroll management and staking — how to build a betting bank, flat vs percentage staking, the Kelly criterion, how much to bet per bet, and surviving a losing run.",
};

export const REVIEWS_HUB = {
  key: "reviews",
  base: "/tipster-reviews",
  label: "Tipster Reviews & Comparisons",
  backLabel: "All review & comparison guides",
  icon: "star",
  eyebrow: "Judge before you buy",
  h1: "Tipster Reviews & Comparisons",
  intro: "How to judge and compare tipsters like a pro. This hub explains how to tell if a tipster is worth it, what a fair review looks like, how to compare two services, free trial vs paid, the red flags in fake reviews, and exactly how we score tipsters.",
  seoTitle: "Tipster Reviews & Comparisons: How to Judge a Tipster",
  seoDescription: "How to review and compare betting tipsters — telling if one is worth it, what a fair review looks like, comparing two services, free trial vs paid, review red flags and how we score.",
};

export const EXCHANGES_HUB = {
  key: "exchanges",
  base: "/betting-exchanges",
  label: "Betting Exchanges",
  backLabel: "All betting exchange guides",
  icon: "target",
  eyebrow: "Back, lay & trade",
  h1: "Betting Exchanges",
  intro: "How betting exchanges work and how to use them well. This hub compares the main UK exchanges, explains laying, liability and commission, Betfair SP and trading, so you can judge exchange-based tipsters and get fairer prices.",
  seoTitle: "Betting Exchanges: Best UK Exchanges, Laying & Commission",
  seoDescription: "The best UK betting exchanges compared, Betfair vs Matchbook vs Smarkets, how to lay a bet, exchange commission and Betfair SP explained in plain English.",
};

export const KNOWLEDGE_HUBS = [
  BASICS_HUB, EVALUATE_HUB, CHOOSING_HUB, REVIEWS_HUB, BEATBOOKIE_HUB, SPORTSTRATEGY_HUB,
  BANKROLL_HUB, PLATFORMS_HUB, SCAM_HUB, SAFETY_HUB, PSYCHOLOGY_HUB, EXCHANGES_HUB,
];
